import { css } from "@emotion/react";
import { Button, Theme, useTheme } from "@mui/material";
import { useCallback } from "react";
import { match } from "ts-pattern";

import { ResponseAnswer } from "~/components/database/detail/database-content/types";
import { responseModalContentRootDesignStyle } from "~/components/database/detail/response-modal-content/design";
import { useCreateResponse } from "~/components/database/detail/response-modal-content/lib";
import { Icon } from "~/components/database/detail/response-modal-content/response-form/AnswerForm";
import { flexRow } from "~/lib/client/design";
import {
  AnswerTypeEnum,
  SpecialWelcomeSaihateContentQuery,
} from "~/lib/graphql/client";
import "~/lib/monkeypatch";
import { DatabaseDetailPageProps } from "~/pages/database/[id]";

const ResponseConfirm = ({
  form,
  responseAnswers,
  stepToEditor,
  stepToSuccess,
}: {
  form:
    | DatabaseDetailPageProps["form"]
    | SpecialWelcomeSaihateContentQuery["forms"][number];
  responseAnswers: ResponseAnswer[];
  stepToEditor: () => void;
  stepToSuccess: () => void;
}) => {
  const theme = useTheme();
  const { isResponseCreating, createResponse } = useCreateResponse({
    form,
    responseAnswers,
  });

  const questionAndResponseByPosition = form.questions
    .map((question) => {
      const responseAnswer = responseAnswers.find(
        (responseAnswer) => responseAnswer.questionId === question.id
      );
      return {
        id: question.position,
        question,
        responseAnswer,
      };
    })
    .indexById();

  const handleCreateResponse = async () => {
    await createResponse();
    stepToSuccess();
  };

  const stringedAnswer = useCallback(
    (
      question: (typeof form.questions)[number],
      responseAnswer: (typeof responseAnswers)[number]
    ) =>
      match<AnswerTypeEnum, string | undefined>(question.answerType)
        .with(AnswerTypeEnum.Text, () => responseAnswer.textAnswer?.value)
        .with(AnswerTypeEnum.LongText, () => responseAnswer.textAnswer?.value)
        .with(AnswerTypeEnum.ShortText, () => responseAnswer.textAnswer?.value)
        .with(AnswerTypeEnum.ImageUrl, () => responseAnswer.textAnswer?.value)
        .with(AnswerTypeEnum.Url, () => responseAnswer.textAnswer?.value)
        .with(AnswerTypeEnum.Number, () =>
          responseAnswer.numberAnswer?.value.toString()
        )
        .with(AnswerTypeEnum.Location, () =>
          responseAnswer.locationAnswer
            ? `${responseAnswer.locationAnswer.latitude}, ${responseAnswer.locationAnswer.longitude}`
            : undefined
        )
        .with(AnswerTypeEnum.Date, () => responseAnswer.dateAnswer?.value)
        .with(AnswerTypeEnum.Reference, () => undefined)
        .exhaustive(),
    [form]
  );

  return (
    <div css={rootStyle}>
      {form.questions.map((question) => {
        const questionAndResponse =
          questionAndResponseByPosition[question.position];
        const responseAnswer = questionAndResponse?.responseAnswer;

        if (!responseAnswer) return null;

        return (
          <div key={question.id} css={questionStyle(theme)}>
            <div css={iconAndTitleStyle(theme)}>
              <Icon answerType={question.answerType} />
              <div>{question.title}</div>
            </div>
            <div css={answerContainerStyle}>
              {stringedAnswer(question, responseAnswer)}
              {question.answerType === AnswerTypeEnum.ImageUrl && (
                <img
                  src={responseAnswer.textAnswer?.value}
                  alt={responseAnswer.textAnswer?.value}
                  css={imageResponseStyle}
                />
              )}
            </div>
          </div>
        );
      })}
      <div css={buttonContainerStyle}>
        <Button
          sx={{
            flexGrow: 0,
          }}
          variant={"outlined"}
          onClick={stepToEditor}
        >
          もどる
        </Button>
        <Button
          sx={{
            flexGrow: 1,
          }}
          variant={"contained"}
          onClick={handleCreateResponse}
        >
          {isResponseCreating ? "投稿中..." : "投稿する"}
        </Button>
      </div>
    </div>
  );
};

const rootStyle = responseModalContentRootDesignStyle;

const questionStyle = (theme: Theme) => css`
  padding: 0.5rem 1.5rem;
  border-bottom: 2px solid ${theme.palette.divider};
`;

const iconAndTitleStyle = (theme: Theme) => css`
  ${flexRow};
  justify-content: flex-start;
  color: ${theme.palette.primary.main};
  font-weight: 600;
`;

const answerContainerStyle = css`
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;

  white-space: pre-wrap;
  word-wrap: break-word;
`;

const buttonContainerStyle = css`
  ${flexRow};
  margin: 0.25rem;
  gap: 0.25rem;
`;

const imageResponseStyle = css`
  max-width: 100%;
  height: auto;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

export default ResponseConfirm;
